"use client";

import { useMemo, useState } from "react";
import { ChevronDown, Database, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

type Service = {
  service_key: string;
  display_name: string;
  domain: string;
  lifecycle_state: string;
  identity_email?: string | null;
  capability_keys?: string[];
  action_keys?: string[];
};
type Backend = {
  backend_service_key: string;
  display_name: string;
  logical_role: string;
  provider_key?: string | null;
  lifecycle_state: string;
  health_state: string;
  identity_email?: string | null;
};
type Resource = {
  entity_key: string;
  display_name: string;
  entity_type_key: string;
  domain?: string | null;
  lifecycle_state: string;
  health_state: string;
  identity_email?: string | null;
  tenant_project_ref?: string | null;
};
type Catalogue = {
  services?: Service[];
  backend_services?: Backend[];
  resources?: Resource[];
  resource_total_count?: number;
  resource_next_offset?: number | null;
} | null;
type CatalogueRow = {
  key: string;
  label: string;
  kind: string;
  state: string;
  health: string;
  email: string;
  detail: string;
  capabilities: string[];
};

/** A projection of OWNER ROOT's existing catalogue, not a second resource register. */
export function OwnerRootServiceCatalogue({
  catalogue, onRefresh, onOpenMarketplace, onLoadMore, loadingMore, syncState = "STALE", lastSyncedAt = null,
}: {
  catalogue: Catalogue;
  onRefresh: () => void;
  onOpenMarketplace: (search: string) => void;
  onLoadMore: () => void;
  loadingMore: boolean;
  syncState?: "CURRENT" | "SYNCING" | "STALE" | "ERROR";
  lastSyncedAt?: string | null;
}) {
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState("");
  const count = {
    resources: catalogue?.resource_total_count ?? catalogue?.resources?.length ?? 0,
    loaded: catalogue?.resources?.length ?? 0,
    services: catalogue?.services?.length ?? 0,
    backendServices: catalogue?.backend_services?.length ?? 0,
  };
  const total = count.resources + count.services + count.backendServices;
  const rows = useMemo(() => {
    const serviceRows: CatalogueRow[] = (catalogue?.services ?? []).map((item) => ({
      key: item.service_key,
      label: item.display_name,
      kind: item.domain || "Service",
      state: item.lifecycle_state,
      health: "—",
      email: item.identity_email ?? "NOT CONNECTED",
      detail: item.service_key,
      capabilities: [...(item.capability_keys ?? []), ...(item.action_keys ?? [])],
    }));
    const backendRows: CatalogueRow[] = (catalogue?.backend_services ?? []).map((item) => ({
      key: item.backend_service_key,
      label: item.display_name,
      kind: item.logical_role || "Backend",
      state: item.lifecycle_state,
      health: item.health_state,
      email: item.identity_email ?? "NOT CONNECTED",
      detail: item.provider_key ?? item.backend_service_key,
      capabilities: [],
    }));
    const resourceRows: CatalogueRow[] = (catalogue?.resources ?? []).map((item) => ({
      key: item.entity_key,
      label: item.display_name,
      kind: item.entity_type_key,
      state: item.lifecycle_state,
      health: item.health_state,
      email: item.identity_email ?? "NOT CONNECTED",
      detail: item.tenant_project_ref ?? item.domain ?? item.entity_key,
      capabilities: [],
    }));
    const term = query.trim().toLowerCase();
    return [...serviceRows, ...backendRows, ...resourceRows].filter((item) =>
      !term || [item.label, item.key, item.kind, item.email, item.detail, ...item.capabilities]
        .join(" ").toLowerCase().includes(term));
  }, [catalogue, query]);
  return (
    <section className="w-full min-w-0 overflow-hidden border-b border-foreground/10 bg-background/95 px-2 py-1.5 sm:rounded-xl sm:border sm:px-4">
      <button type="button" className="flex min-h-9 w-full items-center gap-2 text-left" onClick={() => setOpened((value) => !value)} aria-expanded={opened}>
        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Database className="size-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold">Universal resources & services</span>
          <span className="hidden text-[11px] text-foreground/50 sm:block">OWNER ROOT · Configure once · Governed reuse</span>
        </span>
        <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
          {total}
        </span>
        <ChevronDown className={`size-4 shrink-0 transition ${opened ? "rotate-180" : ""}`} />
      </button>
      {opened && (
        <div className="space-y-3 pt-3">
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-2">
            <div className="relative min-w-0">
              <Search className="absolute left-3 top-3.5 size-4 text-foreground/40" />
              <input aria-label="Search universal resources and services"
                className="h-11 w-full rounded-xl border border-foreground/10 bg-background pl-9 pr-2 text-sm outline-none focus:border-primary"
                placeholder="Search resources…" value={query} onChange={(event) => setQuery(event.target.value)} />
            </div>
            <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label="Refresh registered resources" title="Refresh registered resources" className="size-11 shrink-0 rounded-xl">
              <RefreshCw className="size-4" />
            </Button>
          </div>
          {rows.length === 0 && (
            <p className="rounded-xl bg-foreground/[0.035] px-3 py-4 text-xs text-foreground/55">
              {catalogue ? "No matching, authorized resources in the current catalogue." : "Loading the OWNER ROOT catalogue…"}
            </p>
          )}
          <p className="text-[11px] leading-5 text-foreground/50">
            {total} catalogue entries = {count.resources} registered resources + {count.services} services + {count.backendServices} backend services.
            {" "}{count.loaded} of {count.resources} resources loaded. Search matches loaded records; load more for additional resources.
          </p>
          <p className="text-[10px] text-foreground/45" role="status">
            Synchronization: {syncState}{lastSyncedAt ? ` · Last confirmed ${new Date(lastSyncedAt).toLocaleTimeString()}` : " · Awaiting authoritative data"}
          </p>
          <div className="max-h-[65dvh] space-y-2 overflow-y-auto overscroll-contain">
            {rows.map((item) => (
              <div key={item.key} className="min-w-0 rounded-xl border border-foreground/10 p-2.5">
                <p className="break-words text-sm font-semibold">{item.label}</p>
                <p className="mt-1 break-all text-[11px] text-foreground/50">{item.kind} · {item.detail}</p>
                <p className="mt-1 break-all text-[11px] text-foreground/60">Email: {item.email}</p>
                <p className="mt-1 text-[11px] text-foreground/60">Lifecycle: {item.state} · Health: {item.health}</p>
                {item.capabilities.length > 0 &&
                  <p className="mt-1 break-words text-[11px] text-foreground/45">Registered capabilities: {item.capabilities.join(" · ")}</p>}
                <Button type="button" variant="outline" size="sm" className="mt-2 min-h-9 w-full min-w-0 whitespace-normal break-words rounded-xl px-2.5 py-2 text-center text-[11px] leading-4"
                  onClick={() => onOpenMarketplace(item.label)}>
                  Find in Marketplace
                </Button>
              </div>
            ))}
          </div>
          {catalogue?.resource_next_offset != null && (
            <Button type="button" variant="outline" className="min-h-11 w-full rounded-xl" disabled={loadingMore} onClick={onLoadMore}>
              {loadingMore ? "Loading resources…" : "Load more registered resources"}
            </Button>
          )}
          <p className="text-[11px] text-foreground/45">
            Catalogue registration is discoverability, not automatic authorization or active provider execution.
            The existing Marketplace evaluates compatibility, capacity, tenant policy and approvals before assignment.
          </p>
        </div>
      )}
    </section>
  );
}
